import { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Grid, Box, IconButton, Tooltip, Typography, TextField, MenuItem, Select, Checkbox, ListItemText } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { Button } from "abs-ui-components";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
import RichTextEditor from "../../RichTextEditor/RichTextEditor";

const ShipEnginesAndFuelConsumers = ({ refetchMonitoringData, vesselDetails, PreFillData, ShipEngines, vesselMstId }) => {
	const [shipEnginesAndFuelConsumers, setShipEnginesAndFuelConsumers] = useState([]);
	const [validationErrors, setValidationErrors] = useState({});
	const [activeAction, setActiveAction] = useState(-1);
	const [fuelRegistry, setFuelRegistry] = useState([]);
	const RowActions = { Edit: 0, Add: 1, Delete: 2 };
	const editorRef = useRef(null);
	const [error, setError] = useState("");
	const [editorContent, setEditorContent] = useState("");
	const [lastSavedContent, setLastSavedContent] = useState("");
	const [isSavingDescription, setIsSavingDescription] = useState(false);

	useEffect(() => {
		if (vesselDetails?.FuelRegistryDtls) {
			setFuelRegistry(vesselDetails.FuelRegistryDtls);
		}
	}, [vesselDetails]);

	useEffect(() => {
		const fetchPrefillData = () => {
			try {
				const rawConsumers = PreFillData?.engineFuelOilConsumerTypeData ?? [];
				const savedEngineData = ShipEngines?.shipEnginesAndFuelOilConsumers?.[0]?.enginesAndFuelOilConsumers ?? [];
				setEditorContent(ShipEngines?.shipEnginesAndFuelOilConsumers?.[0]?.consumersDescription || "");

				const merged = rawConsumers.map((consumer, index) => {
					const consumerName = consumer.enginesOrOtherFuelOilConsumerType ?? "";
					const lowerName = consumerName.toLowerCase();
					const matchedSaved = savedEngineData.find((saved) => String(saved.fuelOilConsumers || "").toLowerCase() === lowerName);
					const power = matchedSaved?.power?.length ? matchedSaved.power : consumer.value != null ? [String(consumer.value)] : [];
					const units =
						lowerName.includes("main engine") || lowerName.includes("aux engine") || lowerName.includes("auxlary ")
							? "kW"
							: matchedSaved?.units || "";
					const fuelOilTypes = Array.isArray(matchedSaved?.fuelOilTypes)
						? matchedSaved.fuelOilTypes
						: typeof matchedSaved?.fuelOilTypes === "string"
						? matchedSaved.fuelOilTypes
								.split(",")
								.map((s) => s.trim())
								.filter(Boolean)
						: [];

					return {
						index: index + 1,
						consumersDescription: ShipEngines?.shipEnginesAndFuelOilConsumers?.[0]?.consumersDescription || "",
						fuelOilConsumers: consumerName,
						type: lowerName.includes("main engine")
							? "Main Engine"
							: lowerName.includes("aux engine")
							? "Aux Engine"
							: lowerName.includes("boiler")
							? "Boiler"
							: "Other",
						power,
						units,
						fuelOilTypes,
						description: matchedSaved?.description || "",
					};
				});

				setShipEnginesAndFuelConsumers(merged);
			} catch (err) {
				emitToasterMsg("error", "Prefill", err?.errorMessage || err.message);
			}
		};

		if (vesselMstId && vesselDetails && Array.isArray(PreFillData?.engineFuelOilConsumerTypeData) && ShipEngines?.shipEnginesAndFuelOilConsumers) {
			fetchPrefillData();
		}
	}, [vesselMstId, vesselDetails, PreFillData, ShipEngines]);
	const resetActiveRow = () => {
		setActiveAction(-1);
		setValidationErrors({});
	};

	const handleFuelActions = (table, row, action) => {
		if (activeAction === -1) {
			if (action === RowActions.Edit) table.setEditingRow(row);
			else if (action === RowActions.Add) table.setCreatingRow(true);
			setActiveAction(action);
		}
	};

	const handleDeleteShipEngines = async (rowIndex) => {
		const updatedData = shipEnginesAndFuelConsumers.filter((_, index) => index !== rowIndex);
		try {
			const res = await callApi({
				method: "POST",
				url: API_ROUTES.ShipEnginesAndFuelOilConsumers(vesselMstId),
				data: updatedData,
			});
			if (res?.isSuccess) {
				emitToasterMsg("success", "Table 4", "Deleted successfully");
				setShipEnginesAndFuelConsumers(updatedData);
				setActiveAction(-1);
				await refetchMonitoringData();
			} else {
				emitToasterMsg("error", "Table 4", res?.errorMessage);
			}
		} catch (error) {
			emitToasterMsg("error", "Table 4", error.errorMessage);
		}
	};

	const handleSaveShipEngines = async ({ values, table, row }) => {
		const isNewRow = row.id?.toString().includes("mrt-row-create");

		const normalizeFuelTypes = (types) => {
			if (Array.isArray(types)) return types.map(String).filter(Boolean);
			if (typeof types === "string")
				return types
					.split(",")
					.map((t) => t.trim())
					.filter(Boolean);
			return [];
		};

		const newEntry = {
			Index: isNewRow ? shipEnginesAndFuelConsumers.length : row.original.Index ?? row.index,
			ConsumersDescription: editorContent || values.consumersDescription?.trim() || "",
			FuelOilConsumers: values.fuelOilConsumers?.trim() || "",
			Power: values.power || [],
			Units: values.fuelOilConsumers?.toLowerCase().includes("engine") ? "kW" : values.units?.trim() || "",
			Description: values.description?.trim() || "",
			FuelOilTypes: normalizeFuelTypes(values.fuelOilTypes || []),
		};

		const normalizeItem = (item) => ({
			Index: item.Index ?? item.index ?? 0,
			FuelOilConsumers: item.FuelOilConsumers ?? item.fuelOilConsumers ?? "",
			Power: item.power || [],
			Units: item.Units ?? item.units ?? "",
			Description: item.Description ?? item.description ?? "",
			FuelOilTypes: Array.isArray(item.FuelOilTypes) ? item.FuelOilTypes : Array.isArray(item.fuelOilTypes) ? item.fuelOilTypes : [],
		});

		const updatedEngines = isNewRow
			? [...shipEnginesAndFuelConsumers.map(normalizeItem), normalizeItem(newEntry)]
			: shipEnginesAndFuelConsumers.map((item) => (item.index === newEntry.index ? normalizeItem(newEntry) : normalizeItem(item)));

		const finalPayload = [
			{
				vesselMstId,
				consumersDescription: editorContent || values.consumersDescription?.trim() || "",
				enginesAndFuelOilConsumers: updatedEngines,
			},
		];

		try {
			const res = await callApi({
				method: "POST",
				url: API_ROUTES.ShipEnginesAndFuelOilConsumers(vesselMstId),
				data: finalPayload,
			});
			if (res?.isSuccess) {
				emitToasterMsg("success", "Table 4", "Saved successfully");
				isNewRow ? table.setCreatingRow(null) : table.setEditingRow(null);
				setActiveAction(-1);
				setValidationErrors({});

				// Only refetch if absolutely necessary
				if (refetchMonitoringData) {
					await refetchMonitoringData();
				}
			} else {
				emitToasterMsg("error", "Table 4", res?.errorMessage || "Save failed");
			}
		} catch (error) {
			console.error("Save error:", error);
			emitToasterMsg("error", "Table 4", error?.errorMessage || error.message || "Unexpected error");
		}
	};
	useEffect(() => {
		if (ShipEngines?.shipEnginesAndFuelOilConsumers?.[0]?.consumersDescription) {
			const desc = ShipEngines.shipEnginesAndFuelOilConsumers[0].consumersDescription;
			setEditorContent(desc);
			setLastSavedContent(desc);
		}
	}, [ShipEngines]);
	const handleSaveRichText = async () => {
		const editorHTML = editorRef.current.getHTML().trim();
		const cleaned = editorHTML
			.replace(/<p><br><\/p>/g, "")
			.replace(/&nbsp;/g, "")
			.trim();
		const hasContent = cleaned.length > 0 && cleaned !== "<p></p>" && cleaned !== "<br>" && cleaned !== "";

		if (!hasContent) {
			setError("Field is required");
			return;
		}

		setIsSavingDescription(true);
		setError("");
		try {
			const updatedEngines = [...shipEnginesAndFuelConsumers];

			const finalPayload = [
				{
					vesselMstId,
					consumersDescription: editorHTML,
					enginesAndFuelOilConsumers: updatedEngines,
				},
			];

			const res = await callApi({
				method: "POST",
				url: API_ROUTES.ShipEnginesAndFuelOilConsumers(vesselMstId),
				data: finalPayload,
			});

			if (res?.isSuccess) {
				emitToasterMsg("success", "Ship Engine Description", "Saved successfully");
				await refetchMonitoringData?.();
				setLastSavedContent(editorHTML);
			} else {
				emitToasterMsg("error", "Description", res?.errorMessage || "Failed to save");
			}
		} catch (err) {
			emitToasterMsg("error", "Description", err?.errorMessage || err.message || "Unexpected error");
		} finally {
			setIsSavingDescription(false);
		}
	};

	const isSaveDisabled = isSavingDescription || editorContent.trim() === "" || editorContent === lastSavedContent;
	const safeValue = (value) => (typeof value === "string" ? value : "");
	const columns = useMemo(
		() => [
			{
				header: "S.No",
				Cell: ({ row }) => row.index + 1,
				size: 10,
				enableEditing: false,
			},
			{
				accessorKey: "fuelOilConsumers",
				header: "Fuel oil consumers",
				enableEditing: false,
				Cell: ({ cell }) => (
					<Tooltip
						title={
							<span>
								Value auto-filled. To update, please modify the details in{" "}
								<a href={`/my-fleet/enroll-vessel/${vesselMstId}`} style={{ color: "blue", textDecoration: "underline" }}>
									MyFleet
								</a>
							</span>
						}
						placement="bottom-start"
						arrow
					>
						<span>{cell.getValue()}</span>
					</Tooltip>
				),
			},
			{
				accessorKey: "power",
				header: "Power",
				enableEditing: (row) => {
					const type = row.original.type?.toLowerCase() ?? "";
					return !(type === "main engine" || type === "aux engine");
				},
				Cell: ({ cell, row }) => {
					const rawValue = cell.getValue();
					const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;

					const type = row.original.type?.toLowerCase() ?? "";
					const shouldShowTooltip = type === "main engine" || type === "aux engine";
					if (!shouldShowTooltip) {
						return <span>{value}</span>;
					}

					return (
						<Tooltip
							title={
								<span>
									Value auto-filled. To update, please modify the details in{" "}
									<a href={`/my-fleet/enroll-vessel/${vesselMstId}`} style={{ color: "blue", textDecoration: "underline" }}>
										MyFleet
									</a>
								</span>
							}
							placement="bottom-start"
							arrow
						>
							<span>{value}</span>
						</Tooltip>
					);
				},
				Edit: ({ row, table }) => {
					const currentValue = Array.isArray(row.original.power) ? row.original.power[0] : row.original.power;

					return (
						<TextField
							value={currentValue || ""}
							onChange={(e) => {
								table.options.meta?.updateData(row.index, "power", [e.target.value]);
							}}
							fullWidth
							variant="standard"
						/>
					);
				},
			},
			{
				accessorKey: "units",
				header: "Units of power",
				enableEditing: (row) => {
					const consumer = row.original.fuelOilConsumers?.toLowerCase() ?? "";
					return !(consumer.startsWith("main engine") || consumer.startsWith("aux engine"));
				},
				Cell: ({ cell, row }) => {
					const consumer = row.original.fuelOilConsumers?.toLowerCase() ?? "";
					const isEngine = consumer.startsWith("main engine") || consumer.startsWith("aux engine");

					return (
						<Tooltip
							title={
								isEngine ? (
									<span>
										Value auto-filled. To update, please modify the details in{" "}
										<a
											href={`/my-fleet/enroll-vessel/${vesselMstId}`}
											style={{ color: "blue", textDecoration: "underline" }}
											target="_blank"
											rel="noopener noreferrer"
										>
											MyFleet
										</a>
									</span>
								) : null
							}
							placement="bottom-start"
							arrow
						>
							<span>{isEngine ? "kW" : cell.getValue() || ""}</span>
						</Tooltip>
					);
				},
				muiEditTextFieldProps: ({ cell, column, row, table }) => ({
					value: cell.getValue() ?? "",
					onChange: (e) => {
						table.options.meta?.updateData?.(row.index, column.id, e.target.value);
					},
				}),
			},
			{
				accessorKey: "description",
				header: "Description",
				muiTableBodyCellEditTextFieldProps: {
					variant: "outlined",
					fullWidth: true,
				},
				Cell: ({ cell }) => cell.getValue() || "",
				Edit: ({ row, table }) => (
					<TextField
						value={row.original.description || ""}
						onChange={(e) => {
							table.options.meta?.updateData(row.index, "description", e.target.value);
						}}
						fullWidth
						variant="standard"
					/>
				),
			},
			{
				accessorKey: "fuelOilTypes",
				header: "Fuel Oil Types",
				Cell: ({ cell }) => {
					const value = cell.getValue() || [];
					const selectedNames = (Array.isArray(value) ? value : [])
						.map((code) => fuelRegistry?.find((f) => f.FuelType === code)?.FuelName || code)
						.join(", ");

					return <span>{selectedNames || "Select Fuel Oil Types"}</span>;
				},
				Edit: ({ row, table, column }) => {
					const currentValues = Array.isArray(row._valuesCache?.[column.id])
						? row._valuesCache[column.id]
						: Array.isArray(row.original[column.id])
						? row.original[column.id]
						: [];

					const handleChange = (event) => {
						const value = typeof event.target.value === "string" ? [event.target.value] : event.target.value;
						table.options.meta?.updateData(row.index, column.id, value);
					};

					return (
						<Select
							multiple
							value={currentValues}
							onChange={handleChange}
							renderValue={(selected) => selected.map((code) => fuelRegistry?.find((f) => f.FuelType === code)?.FuelName || code).join(", ")}
							fullWidth
							variant="standard"
						>
							{fuelRegistry.map((fuel) => (
								<MenuItem key={fuel.FuelType} value={fuel.FuelType}>
									<Checkbox checked={currentValues.includes(fuel.FuelType)} />
									<ListItemText primary={fuel.FuelName} />
								</MenuItem>
							))}
						</Select>
					);
				},
			},
		],
		[validationErrors, fuelRegistry]
	);

	const table = useMaterialReactTable({
		columns,
		data: shipEnginesAndFuelConsumers,
		createDisplayMode: "row",
		editDisplayMode: "row",
		enableEditing: true,
		enableStickyHeader: true,
		enableTopToolbar: false,
		enableBottomToolbar: false,
		enableColumnActions: false,
		enableGlobalFilter: false,
		enableSorting: false,
		enablePagination: false,
		enableRowSelection: false,
		enableColumnPinning: false,
		enableDensityToggle: false,
		enableFullScreenToggle: false,
		meta: {
			updateData: (rowIndex, columnId, value) => {
				setShipEnginesAndFuelConsumers((prev) => prev.map((row, i) => (i === rowIndex ? { ...row, [columnId]: value } : row)));
			},
		},

		muiTableHeadCellProps: {
			sx: {
				color: "rgb(255 255 255)",
				backgroundColor: "#042a4c",
				position: "sticky",
				borderRight: "1px solid lightgray",
				"&:last-child": { borderRight: "none" },
			},
		},
		muiTableContainerProps: { sx: { maxHeight: "520px" } },
		muiTableBodyCellProps: {
			sx: {
				borderRight: "1px solid lightgray",
				"&:last-child": { borderRight: "none" },
			},
		},
		muiTableBodyRowProps: {
			sx: { "&:not(:last-of-type)": { borderBottom: "1px solid lightgray" }, verticalAlign: "top" },
		},
		muiTableHeadRowProps: {
			sx: { borderBottom: "2px solid lightgray" },
		},
		onCreatingRowSave: handleSaveShipEngines,
		onEditingRowSave: handleSaveShipEngines,
		onEditingRowCancel: resetActiveRow,
		onCreatingRowCancel: resetActiveRow,
		renderRowActions: ({ row, table }) => (
			<Box sx={{ display: "flex", gap: "1rem" }}>
				<Tooltip title="Edit">
					<IconButton onClick={() => handleFuelActions(table, row, RowActions.Edit)}>
						<EditIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Delete">
					<IconButton color="error" onClick={() => handleDeleteShipEngines(row.index)}></IconButton>
				</Tooltip>
			</Box>
		),
	});

	return (
		<div className="fuel-registry-b3 mt-1">
			<Box mt={3}>
				<Grid container spacing={2} alignItems="flex-start">
					<Grid item xs={12} md={2}>
						<Typography variant="body2" fontWeight={600} mb={1}>
							Description <span style={{ color: "red" }}>*</span>
						</Typography>
					</Grid>
					<Grid item xs={12} md={9}>
						<Box border="1px solid #ccc" borderRadius={1} p={1} sx={{ background: "#fff" }}>
							<RichTextEditor
								ref={editorRef}
								value={editorContent || ""}
								onChange={(content) => {
									setEditorContent(content);
									setError("");
								}}
								placeholder="Enter description for ship engines..."
							/>
						</Box>
						{error && (
							<Typography variant="caption" color="error" mt={1}>
								{error}
							</Typography>
						)}
						<Box mt={2} mb={2} display="flex" justifyContent="flext-start">
							<Button onClick={handleSaveRichText} disabled={isSaveDisabled} size="small">
								{isSavingDescription ? "Save" : "Save"}
							</Button>
						</Box>
					</Grid>
				</Grid>
			</Box>
			<MaterialReactTable table={table} />
		</div>
	);
};

export default ShipEnginesAndFuelConsumers;
